import { ApiResponse } from './api';
import { Asset } from './asset';
import { Benchmark } from './benchmark';
import { FundGeographicalFocus } from './fundGeographicalFocus';
import { FundManager } from './fundManager';
import { FundAssetClass } from './fundAssetClass';
import { HoldingCompany } from './holdingCompany';
import { FundValuationDay } from './fundValuationDay';
import { FundValuationFrequency } from './fundValuationFrequency';

export enum FundRiskLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  LOW_TO_MEDIUM = 'LOW_TO_MEDIUM',
  MEDIUM_TO_HIGH = 'MEDIUM_TO_HIGH',
}

export interface FundRiskLevelOption {
  id: number;
  name: FundRiskLevel;
}

export enum FundCategory {
  MUTUAL_FUND = 'Mutual Fund',
  PRIVATE_FUND = 'Private Fund',
  ETF = 'ETF',
}

export interface FundCategoryOption {
  id: number;
  name: FundCategory;
}

export enum FundSource {
  YAHOO_FINANCE = 'Yahoo Finance',
  ALPACA_MARKET = 'Alpaca Market',
}

export interface FundSourceOption {
  id: number;
  name: FundSource;
}

export enum FundStatus {
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
}

export interface FundStatusOption {
  id: number;
  name: FundStatus;
}

export enum FundType {
  OPEN_ENDED_FUND = 'Open-Ended-Fund',
  CLOSE_ENDED_FUND = 'Close-Ended-Fund',
}

export interface FundTypeOption {
  id: number;
  name: FundType;
}

export interface FundCurrencyOption {
  id: number;
  name: string;
}

export enum FundAttachmentType {
  FACT_SHEET = 'Fact Sheet',
  FACT_SHEET_AR = 'Fact Sheet Ar',
}

export interface FundAttachment {
  id: number;
  documentLink: string;
  type: FundAttachmentType;
}

export interface FundNavDataItem {
  id: number;
  nav: number;
  dateTime: Date | string;
}

export interface GicsOption {
  id: number;
  nameEn: string;
  nameAr: string;
  code: string;
}

export interface GeoCategoryOption {
  id: number;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  createdAt: string;
  updatedAt: string;
}

export interface FundGics {
  id?: number;
  gics: GicsOption;
  weight: number;
}

export enum FundStrategy {
  Active = 'Active',
  Passive = 'Passive',
  Balanced = 'Balanced',
}

export type GicsWeight = {
  gics: number;
  weight: number;
};

export interface FundHoldingCompany {
  id?: number;
  company: HoldingCompany;
  sortOrder: number;
}

export type HoldingCompanySortOrder = {
  company: number;
  sortOrder: number;
};

export type IndexFund = {
  id: number;
  bloombergTicker: string;
  code: string;
  nameEn: string;
  nameAr: string;
  secondNameEn: string;
  secondNameAr: string;
  asset: Asset;
  source: FundSourceOption;
  // gics: FundGics[];
  gics: GicsWeight[];
  // companies: FundHoldingCompany[];
  companies: HoldingCompanySortOrder[];
  assetClass: FundAssetClass;
  strategy: FundStrategy;
  manager: FundManager;
  objectiveEn: string;
  objectiveAr: string;
  geographicalFocus: FundGeographicalFocus;
  shariaaComplaint: boolean;
  allowFractions: boolean;
  customDisplay: boolean;
  expenseRatio: number;
  valuationDay: FundValuationDay;
  valuationFrequency: FundValuationFrequency;
  type: FundTypeOption;
  category: FundCategoryOption;
  currency: FundCurrencyOption;
  status: FundStatusOption;
  netAssetValuePerUnit: number;
  originalNav: number;
  fxRate: number;
  expectedReturn: number;
  assetUnderManagement: number;
  inceptionDate: Date;
  managementFee: number;
  minimumSubscription: number;
  minimumRedemption: number;
  subscriptionFee: number;
  redemptionFee: number;
  otherFee: number;
  benchmark: Benchmark;
  topHoldings: string;
  topHoldingsAr: string;
  numberHoldings: number;
  riskLevel: FundRiskLevelOption;
  y1mReturn: number;
  y3mReturn: number;
  y5mReturn: number;
  y1mStdev: number;
  y3mStdev: number;
  y5mStdev: number;
  y1mReturnRisk: number;
  y3mReturnRisk: number;
  y5mReturnRisk: number;
  attachments: FundAttachment[];
  createdAt: Date | string;
  updatedAt: Date | string;
  isTradable?: boolean | null;
  alpacaAssetId?: string | null;
  tradeabilityCheckedAt?: Date | string | null;
  tradeabilitySource?: string | null;
  geoCategory?: GeoCategoryOption | null;
  factsheetStatus?: string | null;
};

export type FundNavHistoryItem = {
  id: number;
  dateTime: string;
  nav: number;
  createdAt: Date | string;
  updatedAt: Date | string;
  indexFund?: IndexFund;
};

export type FetchIndexFundResponse = ApiResponse<IndexFund>;
export type FetchIndexFundsResponse = ApiResponse<{ items: IndexFund[]; totalItems: number }>;
export type FetchFundNabHistoryResponse = ApiResponse<{ items: FundNavHistoryItem[]; totalItems: number }>;

export type FetchFundRiskLevelOptionsResponse = ApiResponse<FundRiskLevelOption[]>;
export type FetchFundCategoryOptionsResponse = ApiResponse<FundCategoryOption[]>;
export type FetchFundStatusOptionsResponse = ApiResponse<FundStatusOption[]>;
export type FetchFundTypeOptionsResponse = ApiResponse<FundTypeOption[]>;
export type FetchGicsOptionsResponse = ApiResponse<GicsOption[]>;
export type FetchFundSourceOptionsResponse = ApiResponse<FundSourceOption[]>;
export type FetchFundNavDataResponse = ApiResponse<FundNavDataItem[]>;

export type FetchGeoCategoryOptionsResponse = ApiResponse<GeoCategoryOption[]>;

export type CreateIndexFundRequest = Omit<
  IndexFund,
  | 'id'
  | 'asset'
  | 'benchmark'
  | 'manager'
  | 'source'
  | 'geographicalFocus'
  | 'valuationDay'
  | 'valuationFrequency'
  | 'type'
  | 'category'
  | 'assetClass'
  | 'currency'
  | 'status'
  | 'riskLevel'
  | 'attachments'
  | 'createdAt'
  | 'updatedAt'
  | 'geoCategory'
> & {
  id?: number;
  asset: number;
  source: number | null;
  manager: number;
  geographicalFocus: number;
  valuationDay: number;
  valuationFrequency: number;
  type: number;
  category: number;
  assetClass: number;
  currency: number;
  status: number;
  riskLevel: number;
  benchmark: number;
  attachments: Omit<FundAttachment, 'id'>[];
  gics: GicsWeight[];
  geoCategory?: number | null;
};
export type CreateIndexFundResponse = ApiResponse<IndexFund>;
export type ImportFundNavDateFile = { indexFundId: number; file: File };
export type ImportFundChartDateFile = { benchmarkId: number; indexFundId: number; file: File };

export type UpdateIndexFundRequest = Omit<CreateIndexFundRequest, 'id' | 'minimumSubscription'> & {
  id: number;
  // Omitted for a Global non-fractionable fund, whose minimum the backend manages
  minimumSubscription?: number;
};
export type UpdateIndexFundAttachment = Optional<FundAttachment, 'id'>;
export type BulkUpdateIndexFundNavs = { id: number; nav: number; originalNav: number };
export type UpdateIndexFundResponse = ApiResponse<IndexFund>;

export type CheckMinimumsRequestItem = { id: number; weight: number };
export type CheckMinimumsRequest = { indexFunds: CheckMinimumsRequestItem[] };
export type CheckMinimumsResponseData = {
  minimumSubscription: number;
  minimumRedemption: number;
  minimumAddFund: number;
};
export type CheckMinimumsResponse = ApiResponse<CheckMinimumsResponseData>;
